import { useMemo, useRef } from 'react';
import type { Ride } from '../../domain/ride';
import { calculateRideMetrics } from '../../domain/ride';
import { useVideoExports } from '../../hooks/use-video-exports';
import { showSubmittedVideo } from '../../services/submission-navigation';
import { useStudioDraft } from '../use-studio-draft';
import { useStudioUndo } from '../use-studio-undo';
import { studioVideoRequest, type StudioDraft } from '../studio-draft';
import { useStudioTimelinePlayback } from '../use-studio-timeline-playback';
import { exportBlockingReasons } from '../export-blocking-reasons';
import { placeTitleForExport } from '../place-title-settings';
import { resolveMapStyle } from '../../domain/map-style';
import { requiredStudioFeatures, studioFeatureCapabilities, unavailableStudioFeatures } from '../../../../shared/studio-capabilities';
import { gimbalExportContext } from '../../domain/gimbal-export-context';
import { resolveExportTiming } from '../../../../renderer/src/export-range';
import { downloadComposition } from './composition-file';

/** Keep the approved workspace on production's storage, account, and export paths. */
export function useProductionEditor(ride: Ride, initialDraft?: StudioDraft, initialDraftSource?: 'file') {
  const storage = useStudioDraft(ride, initialDraft, initialDraftSource);
  const history = useStudioUndo(storage);
  const { draft } = storage; const { update } = history;
  const exports = useVideoExports(true); const social = exports.socialLayoutsSupported;
  const metrics = useMemo(() => ride.metrics ?? calculateRideMetrics(ride.points), [ride]);
  const request = useMemo(() => studioVideoRequest(ride, draft, social, metrics.distanceKilometers),
    [ride, draft.cameraStyleVersion, draft.placeTitle, draft.exportRange, draft.title, draft.camera, draft.distance, draft.cameraGimbal, draft.cameraAngle,
      draft.depthOfField, draft.cinematicEnabled, draft.cinematicSequence, draft.line, draft.mapStyle, draft.trackAppearance,
      draft.hidePlaces, draft.terrainExaggeration, draft.unitSystem, draft.timing, draft.preset, draft.appearances, social, metrics.distanceKilometers]);
  const playback = useStudioTimelinePlayback(ride, { draft, update }, request, storage.update);
  const timing = resolveExportTiming(request);
  const capabilities = exports.mode === 'local' && (!exports.commerce || exports.commerce.status === 'local')
    ? studioFeatureCapabilities('admin') : exports.commerce?.entitlement?.featureCapabilities;
  const proFeatures = unavailableStudioFeatures(requiredStudioFeatures(request, gimbalExportContext({ ...ride, points: ride.sourcePoints ?? ride.points }, request)), capabilities);
  const error = !draft.title.trim() ? 'Add a trace title in Video Setup.'
    : draft.placeTitle.enabled && !placeTitleForExport(draft.placeTitle) ? 'Complete the opening place title in Video Setup.'
    : playback.rangeInvalid ? 'Correct the highlighted IN/OUT timecode.'
    : timing.sourceFrames < 150 ? 'Select at least five seconds of source footage.'
    : storage.needsReview ? 'Submission is paused until you review the changed settings.'
    : storage.conflict ? 'Resolve the saved-settings conflict before submitting.'
    : draft.cinematicEnabled && playback.editing.error ? playback.editing.error : '';
  const blockingReasons = exportBlockingReasons(exports, timing.billedSeconds, {
    error, playing: playback.playing, proFeatures: proFeatures.length > 0,
    previewOnly: !!resolveMapStyle(draft.mapStyle).previewOnly,
  });
  const disabled = blockingReasons.length > 0;
  const submitting = useRef(false);
  const submit = async (ready: boolean) => {
    if (disabled || !ready || submitting.current) return;
    submitting.current = true; storage.flush();
    try { const id = storage.renderProject ? await storage.renderProject() : await exports.start(structuredClone(request)); if (id && exports.mode === 'cloud') showSubmittedVideo(id); }
    finally { submitting.current = false; }
  };
  return { storage, history, draft, update, exports, social, metrics, request, playback, timing, proFeatures, error,
    disabled, blockingReasons, submit,
    download: () => { storage.flush(); downloadComposition(ride, draft, social); } };
}
